import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {
  selectLocationEntities,
  selectSpecialHoursEntities,
  selectFacilityTypeEntities
} from 'app/selectors/EntitiesSelector';
import { selectActivePreferredBranches } from 'app/selectors/LibrarySelector';
import getActiveHours from 'app/helpers/locations/getActiveHours';

// Handles additional filters not currently supported by the api gateway
function filterLocations(locations, specialHoursEntities, query) {
  return locations.filter(location => {
    if (query.get('open')) {
      const specialHours = location.get('specialHoursIds').map(id => specialHoursEntities.get(id));
      const activeHours = getActiveHours(location, specialHours);
      if (!activeHours?.get('openTime')) return false;
    }

    if (query.get('name')) {
      const regex = new RegExp(query.get('name'), 'i');
      if (!regex.test(location.get('name'))) return false;
    }

    return true;
  });
}

const getLocationsSearchState = state => state.getIn(['locations', 'locationsSearch'], Immutable.Map());

export const selectLocations = createSelector(
  [getLocationsSearchState, selectLocationEntities, selectSpecialHoursEntities],
  (locationsSearch, locationEntities, specialHoursEntities) => {
    const query = locationsSearch.get('query', Immutable.Map());
    const locations = locationsSearch.get('results', Immutable.List()).map(id => locationEntities.get(id));
    return filterLocations(locations, specialHoursEntities, query);
  }
);

export const selectPreferredLocations = createSelector(
  [selectActivePreferredBranches, selectLocationEntities],
  (preferredBranches, locationEntities) => {
    return preferredBranches
      .map(preferredBranch => preferredBranch.get('code'))
      .map(locationId => locationEntities.get(locationId))
      .filter(Boolean);
  }
);

export const selectLocationIdToIsPreferred = createSelector([selectPreferredLocations], preferredLocations => {
  const locationIdToIsPreferred = preferredLocations.reduce((acc, preferredLocation) => {
    acc[preferredLocation.get('id')] = true;
    return acc;
  }, {});

  return Immutable.Map(locationIdToIsPreferred);
});

export const selectOrderedLocations = createSelector(
  [selectLocationIdToIsPreferred, selectLocations],
  (locationIdToIsPreferred, locations) => {
    if (!locationIdToIsPreferred.size) {
      return locations;
    }

    const { preferred, regular } = locations.reduce(
      (acc, location) => {
        const locationId = location.get('id');
        const isPreferred = locationIdToIsPreferred.get(locationId, false);

        if (isPreferred) {
          acc.preferred = acc.preferred.push(location);
        } else {
          acc.regular = acc.regular.push(location);
        }

        return acc;
      },
      { preferred: Immutable.List(), regular: Immutable.List() }
    );

    return preferred.concat(regular);
  }
);

export const selectDistances = createSelector([getLocationsSearchState], locationsSearchState =>
  locationsSearchState.get('distances', null)
);

export const selectNearPoint = createSelector([getLocationsSearchState], locationsSearchState =>
  locationsSearchState.get('nearPoint', null)
);

const selectSearchQuery = createSelector([getLocationsSearchState], locationsSearchState =>
  locationsSearchState.get('query', Immutable.Map())
);

export const selectSystemOfMeasurement = createSelector([getLocationsSearchState], locationsSearchState =>
  locationsSearchState.getIn(['eventsLibraryInfo', 'systemOfMeasurement'], null)
);

export const selectDefaultMapState = createSelector([getLocationsSearchState], locationsSearchState =>
  locationsSearchState.getIn(['eventsLibraryInfo', 'defaultMapState'], null)
);

export const selectActiveFilters = createSelector(
  [selectSearchQuery, selectFacilityTypeEntities],
  (query, facilityTypes) => {
    function getAppliedFilters(filter, value) {
      switch (filter) {
        case 'facilities':
          return Immutable.List(
            value.split(',').map(id => {
              return Immutable.Map({
                label: facilityTypes.getIn([id, 'label']),
                value: id
              });
            })
          );
        default:
          return Immutable.Map({
            label: filter,
            value
          });
      }
    }
    return query.map((value, filter) => {
      return getAppliedFilters(filter, value);
    });
  }
);

export default {
  selectLocations,
  selectActiveFilters,
  selectLocationIdToIsPreferred,
  selectOrderedLocations,
  selectPreferredLocations,
  selectNearPoint,
  selectDistances,
  selectSystemOfMeasurement,
  selectDefaultMapState
};
